import { gql } from 'graphql-request';
import { createSignal, onMount, Show, useContext } from 'solid-js';
import { AppContext } from '../../app-context-provider/app-context-provider';
import { Section } from '../../grid-system/section/section';
import { Button } from '../../ui-components/button/button';
import { Heading } from '../../ui-components/heading/heading';
import { LoadingSpinner } from '../loading-spinner/loading-spinner';
import { fieldShellClasses, FormFieldLabel } from '../forms-tw/forms-fields/primitives';
import { cviSemantic } from '../../style/theme';

export const requestPasswordResetMutation = gql`
    mutation requestPasswordReset(
        $email: String!,
        $clientOrigin: String!,
        $resetPasswordUrl: String!
    ) {
        requestPasswordReset(
            email: $email
            clientOrigin: $clientOrigin
            resetPasswordUrl: $resetPasswordUrl
        ) {
            status
            message
        }
    }
`;

export const careRequestPasswordResetMutation = gql`
    mutation careRequestPasswordReset(
        $email: String!,
        $clientOrigin: String!
        $resetPasswordUrl: String!
        $site: String!
    ) {
        careRequestPasswordReset(
            email: $email
            clientOrigin: $clientOrigin
            resetPasswordUrl: $resetPasswordUrl
            site: $site
        ) {
            status
            message
        }
    }
`;

export const RequestPasswordReset = () => {
    const { graphQlClient, localize, pageResetPasswordUrl, siteInfo } = useContext(AppContext);
    const [email,setEmail] = createSignal('');
    const [error,setError] = createSignal('');
    const [message, setMessage] = createSignal('');
    const [loading, setLoading] = createSignal(false);
    let emailInputRef: HTMLInputElement | undefined;

    onMount(() => emailInputRef?.focus());

    const submitResetPasswordForm = async () => {
        if (!email()) {
            setError(`${localize('email', 'Email')} ${localize('is-required', 'is required')}`);
            return;
        }

        setLoading(true);

        const isCareSite = siteInfo.siteType === 'atos-care';        
        const query = isCareSite ? careRequestPasswordResetMutation : requestPasswordResetMutation;
        const origin = window.location.origin;

        const vars = {
            email: email(),
            resetPasswordUrl: pageResetPasswordUrl,
            clientOrigin: origin,
            site: siteInfo.siteSlug
        };

        const response = await graphQlClient(query, vars, false);
        const { status, message } = isCareSite ? response.careRequestPasswordReset : response.requestPasswordReset;

        setLoading(false);

        if (status !== 'OK') {
            if (message === 'user-not-found') {
                setError(localize('request-password-user-not-found', 'No user with this email exists. Please register a new account.'));
            } else {
                setError(localize('error-has-occurred', 'An error has occurred, please try again later'));
            }
            return;
        }

        setMessage(localize('password-reset-request-success-message', 'Password reset request sent. Check your email for further instructions'));
    };

    return (
        <Section widthType="bgFull" templateShorthand={[12]}>
            <div class="mx-auto w-full max-w-cvi-form-column">
                <Heading tag='h1'>{localize('request-password-reset', 'Request password reset')}</Heading>

                <Show when={error()}>
                    <div class="mb-cvi-8 body-sm font-cvi-regular text-cvi-fg-danger">
                        {error()}
                    </div>
                </Show>

                <Show when={message()}>
                    <div class="body-sm font-cvi-regular text-cvi-fg-default">
                        {message()}
                    </div>
                </Show>

                <Show when={!message()}>
                    <form
                        onSubmit={(e) => {
                            e.preventDefault();
                            submitResetPasswordForm();
                        }}
                    >
                        <FormFieldLabel for="request-password-reset-email">
                            {localize('email', 'Email')}
                        </FormFieldLabel>

                        <input
                            ref={emailInputRef}
                            id="request-password-reset-email"
                            value={email()}
                            onInput={(e) => {
                                setError('');
                                setEmail(e.currentTarget.value);
                            }}
                            type="email"
                            name="email"
                            autocomplete="email"
                            class={fieldShellClasses(!!error())}
                        />

                        <div class="mt-cvi-10">
                            <Button
                                label={localize('submit-request-password-reset-form-button-text', 'Submit request')}
                                type="submit"
                                disabled={loading()}
                                noBlockSpacing={true}
                                customCss="width: 100%"
                            />
                        </div>

                        <Show when={loading()}>
                            <div class="mt-cvi-6 flex justify-center">
                                <LoadingSpinner color={cviSemantic.fgBrand} />
                            </div>
                        </Show>
                    </form>
                </Show>
            </div>
        </Section>
    );
};
